import { useCallback, useEffect, useMemo } from "react";
import {
	EditQuizStep,
	getEditQuizStepName,
	isProctoringOptionCategory,
	RenderableProctoringKey,
} from "../proctoring-options/option-text/ProctoringOptionText";
import { StepConfig } from "../../core/stepper/Stepper";
import { EditQuizPageProps } from "../editQuiz";
import { UseEditQuizStateReturn } from "./state/useEditQuizState";
import { ProctoringMethod } from "@verity/proctoring-option/types";
import { checkIfVerityAssessment, isThirdPartyStepValid } from "../editQuizHelper";

type UseEditQuizNavigationProps = Pick<
	EditQuizPageProps,
	| "proctoringAssessmentConfiguration"
	| "enforcedProfilesOnly"
	| "setProctoringAssessmentConfiguration"
	| "onBackEnabledChange"
	| "onNextEnabledChange"
	| "setIsFinalPage"
	| "quizType"
	| "setIsFirstPage"
	| "entryPoint"
> &
	Pick<UseEditQuizStateReturn, "nav" | "live" | "setting" | "profile">;

/**
 * Handles stepper, next, back buttons.
 * Originally in editQuiz.tsx
 */
export default function useEditQuizNavigation({
	proctoringAssessmentConfiguration,
	enforcedProfilesOnly,
	setProctoringAssessmentConfiguration,
	onBackEnabledChange,
	onNextEnabledChange,
	setIsFinalPage,
	quizType,
	setIsFirstPage,
	entryPoint,
	...props
}: UseEditQuizNavigationProps) {
	const { verityInstituteSettings, allowedCategories, enabledProctoringOptions, proctoringData, loading } =
		props.setting.state;
	const { isProctoringOptionAllowed } = props.setting.action;
	const { validateLiveSession, resetConfirmationMessage } = props.live.action;
	const {
		focusStep,
		handleInitialNavigation,
		resetStickyPage,
		handleFocusProctoringOption,
		shouldStartOnThirdPartyStep,
		markMultipleStepsCompleted,
	} = props.nav.action;
	const { setCurPageValue } = props.nav.internal;
	const { curPageValue, shouldStartOnLastStep, stickyPageName, completedStepsSet } = props.nav.state;
	const { activeProfile, canShowProfileStep, shouldShowPlatformConfigStep } = props.profile.state;

	// whenever step changes, focus the modal body
	useEffect(() => {
		focusStep();
	}, [curPageValue, focusStep]);

	const fixedPageNames = useMemo(() => {
		const pageNames: EditQuizStep[] = [];

		const isVerityAssessment = checkIfVerityAssessment(quizType);

		// Verity Assessment is technically a third party, but we don't want users to configure it
		if (proctoringAssessmentConfiguration?.thirdPartyAssessmentOption && !isVerityAssessment) {
			pageNames.push("thirdParty");
		}
		if (!verityInstituteSettings?.liteMode && quizType !== "standalone") {
			pageNames.push("method");
		}

		if (quizType === "standalone" && entryPoint === "lti-page") {
			pageNames.push("assessmentDetails");
			pageNames.push("inviteTestTakers");
		}

		if (canShowProfileStep) {
			pageNames.push("profile");
		}

		pageNames.push("accommodations");

		// Verity Assessment from the lti-page has its separate review step. But if in a modal, must show review step
		if (!isVerityAssessment || (isVerityAssessment && (entryPoint === "lms-modal" || entryPoint === "lti-modal"))) {
			pageNames.push("confirmation");
		}

		return pageNames;
	}, [
		canShowProfileStep,
		entryPoint,
		proctoringAssessmentConfiguration?.thirdPartyAssessmentOption,
		quizType,
		verityInstituteSettings?.liteMode,
	]);

	const getLiveSessionsStepIndex = useCallback(() => {
		return fixedPageNames.indexOf("method") + 1;
	}, [fixedPageNames]);

	// dynamically injecting pages
	const totalPageNames = useMemo(() => {
		const finalSequence: EditQuizStep[] = [];

		fixedPageNames.forEach((page) => {
			finalSequence.push(page);

			if (page === "method" && proctoringAssessmentConfiguration?.isLiveProctored) {
				finalSequence.push("liveSessions");
			}

			const isProfileSelectionStep = page === "profile";
			const isFallbackInjectionPoint =
				!fixedPageNames.includes("profile") &&
				(page === "method" ||
					(!fixedPageNames.includes("method") && (page === "thirdParty" || page === "inviteTestTakers")));

			const shouldInjectAfterProfile = isProfileSelectionStep && !enforcedProfilesOnly && shouldShowPlatformConfigStep;

			// If there is no profile step (because they haven't created any profiles and there is no presets), show platform and config
			const shouldInjectBecauseNoProfiles = !canShowProfileStep && isFallbackInjectionPoint && !enforcedProfilesOnly;

			if (shouldInjectAfterProfile || shouldInjectBecauseNoProfiles) {
				finalSequence.push("platform", "configuration");
				// Inject the sub-categories immediately
				allowedCategories.forEach((cat) => finalSequence.push(cat as EditQuizStep));
			}
		});

		return finalSequence;
	}, [
		fixedPageNames,
		proctoringAssessmentConfiguration?.isLiveProctored,
		enforcedProfilesOnly,
		shouldShowPlatformConfigStep,
		canShowProfileStep,
		allowedCategories,
	]);

	const stepConfig = useMemo(() => {
		const config: StepConfig<EditQuizStep>[] = [];

		totalPageNames.forEach((pageName) => {
			config.push({
				key: pageName,
				label: getEditQuizStepName(pageName),
				// If it's a category, its parent is "Configuration"
				...(isProctoringOptionCategory(pageName) && { parentSubstep: "configuration" as EditQuizStep }),
			});
		});

		return config;
	}, [totalPageNames]);

	const curPageName: EditQuizStep = useMemo(() => totalPageNames[curPageValue], [curPageValue, totalPageNames]);
	const totalPageIndex = useMemo(() => totalPageNames.length - 1, [totalPageNames.length]);

	/**Sets all steps up to the targetIndex as completed */
	const setAllPrevStepCompleted = useCallback(
		(targetIndex: number) => {
			// We take everything from index 0 up to the target index
			const skippedPages = totalPageNames.slice(0, targetIndex);
			markMultipleStepsCompleted(skippedPages);
		},
		[markMultipleStepsCompleted, totalPageNames],
	);

	/**
	 * Pass in the step to go to. This function will update necessary state, and do validation checks.
	 */
	const navigateToStep = useCallback(
		(targetStep: number) => {
			resetStickyPage();

			setCurPageValue((prevIndex: number) => {
				const hasCategories = allowedCategories.length > 0;

				const isMovingForward = targetStep > prevIndex;
				const targetPageName = totalPageNames[targetStep];

				let finalTarget = targetStep;

				// Handle the Configuration skip
				if (targetPageName === "configuration" && hasCategories) {
					finalTarget = isMovingForward ? targetStep + 1 : targetStep - 1;
				}

				const safeTarget = Math.max(0, Math.min(totalPageIndex, finalTarget));

				// Logic check: if we aren't actually moving, do nothing
				if (safeTarget === prevIndex) return prevIndex;

				// Validation (Forward only)
				if (isMovingForward) {
					const prevPageName = totalPageNames[prevIndex];
					if (prevPageName === "liveSessions" && !validateLiveSession()) {
						return prevIndex; // Stay where we are
					}

					setAllPrevStepCompleted(targetStep);
				}

				return safeTarget;
			});
		},
		[
			resetStickyPage,
			setCurPageValue,
			allowedCategories.length,
			totalPageNames,
			totalPageIndex,
			validateLiveSession,
			setAllPrevStepCompleted,
		],
	);

	const handleBackButton = useCallback(() => {
		navigateToStep(curPageValue - 1);
	}, [curPageValue, navigateToStep]);

	const handleGoToStep = useCallback(
		(pageName: EditQuizStep, proctoringOptionToFocus?: RenderableProctoringKey) => {
			const index = totalPageNames.indexOf(pageName);
			if (index >= 0) {
				navigateToStep(index);

				if (proctoringOptionToFocus) {
					handleFocusProctoringOption(proctoringOptionToFocus);
				}
			}
		},
		[handleFocusProctoringOption, navigateToStep, totalPageNames],
	);

	const handleNextButton = useCallback(() => {
		// Handle navigation after user selects their profile
		if (curPageName === "profile" && activeProfile) {
			const targetIndex = totalPageNames.indexOf("accommodations");
			navigateToStep(targetIndex);
			return;
		}
		navigateToStep(curPageValue + 1);
	}, [activeProfile, curPageName, curPageValue, navigateToStep, totalPageNames]);

	const getStepKeyByIndex = useCallback((index: number) => totalPageNames[index], [totalPageNames]);

	const handleConfirmLiveWarning = useCallback(() => {
		resetConfirmationMessage();
		if (curPageName === "liveSessions") {
			handleNextButton();
		}
	}, [curPageName, handleNextButton, resetConfirmationMessage]);

	const completedStepIndexes = useMemo(() => {
		return stepConfig
			.map((step, i) => (completedStepsSet.has(step.key) ? i : null))
			.filter((i): i is number => i !== null);
	}, [completedStepsSet, stepConfig]);

	// Handles proctoring method validation and forces user to live step is something went wrong
	useEffect(() => {
		let proctoringMethod: ProctoringMethod;
		if (proctoringAssessmentConfiguration?.isLiveProctored) {
			proctoringMethod = "live";
		} else if (proctoringAssessmentConfiguration?.isLivePopIn) {
			if (proctoringAssessmentConfiguration.enablePostReview) {
				proctoringMethod = "pop-in-post-review";
			} else {
				proctoringMethod = "pop-in";
			}
		} else {
			proctoringMethod = "automated";
		}
		if (!isProctoringOptionAllowed("proctoringMethod", proctoringMethod)) {
			const allowedProctoringMethod = enabledProctoringOptions.proctoringMethod[0];
			switch (allowedProctoringMethod) {
				case "automated":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: false,
						enablePostReview: false,
					});
					break;
				case "live": {
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: true,
						isLivePopIn: false,
						enablePostReview: false,
					});
					const liveSessionsStepIndex = getLiveSessionsStepIndex();
					if (curPageValue > liveSessionsStepIndex) {
						navigateToStep(liveSessionsStepIndex);
					}
					break;
				}
				case "pop-in":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: true,
						enablePostReview: false,
					});
					break;
				case "pop-in-post-review":
					setProctoringAssessmentConfiguration({
						...proctoringAssessmentConfiguration,
						isLiveProctored: false,
						isLivePopIn: true,
						enablePostReview: true,
					});
					break;
			}
		}
	}, [
		proctoringAssessmentConfiguration,
		isProctoringOptionAllowed,
		enabledProctoringOptions.proctoringMethod,
		setProctoringAssessmentConfiguration,
		getLiveSessionsStepIndex,
		curPageValue,
		navigateToStep,
	]);

	// Force user to be on sticky page
	useEffect(() => {
		if (stickyPageName) {
			const newIndex = totalPageNames.indexOf(stickyPageName);

			if (newIndex !== -1) {
				// 1. Update the current page index
				if (newIndex !== curPageValue) {
					setCurPageValue(newIndex);
				}

				// 2. Sync the Completed Set
				setAllPrevStepCompleted(newIndex);
			}
		}
	}, [totalPageNames, stickyPageName, curPageValue, setCurPageValue, setAllPrevStepCompleted]);

	// Initial navigation
	useEffect(() => {
		if (loading || totalPageNames.length < 1) return;

		const startOnThirdParty = shouldStartOnThirdPartyStep(proctoringData.thirdPartyPlatforms);
		const lastPage = totalPageNames[totalPageNames.length - 1];

		if (shouldStartOnLastStep || startOnThirdParty) {
			// Third party gets higher priority
			handleInitialNavigation(startOnThirdParty ? "thirdParty" : lastPage);
		}
	}, [
		handleInitialNavigation,
		shouldStartOnLastStep,
		totalPageNames,
		loading,
		shouldStartOnThirdPartyStep,
		proctoringData.thirdPartyPlatforms,
	]);

	// Calls callback functions related to enabling/disabling back/next buttons
	useEffect(() => {
		onBackEnabledChange?.({ enabled: curPageValue > 0 && !loading });
		setIsFirstPage?.(curPageValue === 0);
		setIsFinalPage?.(curPageValue === totalPageIndex);

		const thirdPartyOkay = isThirdPartyStepValid(proctoringData.thirdPartyPlatforms, proctoringAssessmentConfiguration);

		const shouldEnableNextButton = thirdPartyOkay && curPageValue < totalPageIndex;

		onNextEnabledChange?.({ enabled: shouldEnableNextButton && !loading });
	}, [
		curPageValue,
		onBackEnabledChange,
		setIsFinalPage,
		onNextEnabledChange,
		totalPageIndex,
		proctoringData.thirdPartyPlatforms,
		proctoringAssessmentConfiguration,
		loading,
	]);

	return {
		curPageName,
		getStepKeyByIndex,
		stepConfig,
		handleBackButton,
		handleGoToStep,
		handleNextButton,
		handleStepChange: navigateToStep,
		completedStepIndexes,
		handleConfirmLiveWarning,
	};
}

export type UseEditQuizNavigationReturn = ReturnType<typeof useEditQuizNavigation>;
